import { c as _objectWithoutProperties, a as _objectSpread2, b as _defineProperty } from './_rollupPluginBabelHelpers-bd852068.js';
import { j as jsx } from './emotion-react.esm-95faa810.js';
import React__default from 'react';
import { VisuallyHidden } from './VisuallyHidden.js';
import { baseTheme } from './baseTheme.js';
import { useTheme } from './useTheme.js';
import { s as scale } from './scale-15569e54.js';
import { typography } from './typography.js';

/**
 * Custom hook returns used component and project themes based on custom theme exist check. Use it for all components defined in `ComponentsTheme`.
 */
var useComponentTheme = function useComponentTheme(name, __theme) {
  var componentTheme;
  var usedTheme;
  var userTheme = useTheme();
  if (userTheme.components && userTheme.components[name]) {
    usedTheme = userTheme;
    componentTheme = userTheme.components[name];
  } else {
    usedTheme = baseTheme;
    componentTheme = baseTheme.components[name];
  }
  componentTheme = __theme || componentTheme;
  return {
    componentTheme: componentTheme,
    usedTheme: usedTheme
  };
};

var _excluded = ["children", "theme", "size", "block", "Icon", "iconAfter", "hidden", "type", "as", "external", "__theme", "css"];
/**
 * Button component.
 *
 * Renders <button /> or <a /> (pass `href`) or any custom element (pass `as`).
 *
 * Define themes and sizes in theme object (`components.Button`) and use them as `theme` / `size` prop values.
 */
var Btn = function Btn(_ref, ref) {
  var _usedTheme$typography, _usedTheme$typography2, _iconCSS;
  var children = _ref.children,
    _ref$theme = _ref.theme,
    theme = _ref$theme === void 0 ? 'primary' : _ref$theme,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'md' : _ref$size,
    _ref$block = _ref.block,
    block = _ref$block === void 0 ? false : _ref$block,
    Icon = _ref.Icon,
    _ref$iconAfter = _ref.iconAfter,
    iconAfter = _ref$iconAfter === void 0 ? false : _ref$iconAfter,
    _ref$hidden = _ref.hidden,
    hidden = _ref$hidden === void 0 ? false : _ref$hidden,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'button' : _ref$type,
    as = _ref.as,
    _ref$external = _ref.external,
    external = _ref$external === void 0 ? false : _ref$external,
    __theme = _ref.__theme,
    css = _ref.css,
    props = _objectWithoutProperties(_ref, _excluded);
  /* Get theme objects. */
  var _useComponentTheme = useComponentTheme('Button', __theme),
    componentTheme = _useComponentTheme.componentTheme,
    usedTheme = _useComponentTheme.usedTheme;
  var buttonTheme = componentTheme;
  if (!buttonTheme.themes[theme]) console.warn("Specify \"".concat(theme, "\" theme. Default values are used instead"));
  if (!buttonTheme.sizes[size]) console.warn("Specify \"".concat(size, "\" size. Default values are used instead"));

  /* Get theme default state properties and merge them with default values. */
  var themeProperties = getThemeProperties(buttonTheme, theme, 'default');
  var sizeProperties = buttonTheme.sizes[size];
  var themeDefaults = {
    borderWidth: themeProperties.border ? 1 : 0,
    borderStyle: 'solid',
    time: 200,
    easing: 'ease',
    color: baseTheme.colors.white,
    bg: baseTheme.colors.black
  };
  var tp = _objectSpread2(_objectSpread2({}, themeDefaults), themeProperties);
  var sizeDefaults = {
    height: scale(6),
    padding: scale(3),
    iconSize: scale(3),
    iconOffset: scale(1)
  };
  var sp = _objectSpread2(_objectSpread2({}, sizeDefaults), sizeProperties);

  /* Define CSS rules from theme properties for default state. */
  var typographyName = sp.typography;
  var typographyCSS = typography(typographyName, usedTheme);
  var pv = getVerticalPaddings(typographyName ? (_usedTheme$typography = usedTheme.typography) === null || _usedTheme$typography === void 0 ? void 0 : (_usedTheme$typography2 = _usedTheme$typography.styles[typographyName]) === null || _usedTheme$typography2 === void 0 ? void 0 : _usedTheme$typography2.desktop : undefined, sp, tp, !!Icon);
  var borderRadius = !tp.half ? tp.borderRadius : sp.height / 2;
  var padding = "".concat(pv, "px ").concat(sp.padding, "px");
  var transition = getTransition(tp.time, tp.easing);
  var defaultCSS = _objectSpread2(_objectSpread2(_objectSpread2({
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: tp.borderWidth,
    borderStyle: tp.borderStyle
  }, typographyCSS), {}, {
    borderRadius: borderRadius,
    padding: padding,
    transition: transition
  }, getStateCSS(tp)), sp.css);

  /* Define CSS rules from theme properties for other states. */
  var themeHoverProperties = getThemeProperties(buttonTheme, theme, 'hover');
  var themeActiveProperties = getThemeProperties(buttonTheme, theme, 'active');
  var themeDisabledProperties = getThemeProperties(buttonTheme, theme, 'disabled');
  var themeFocusProperties = getThemeProperties(buttonTheme, theme, 'focus');
  var statesCSS = {
    ':hover': _objectSpread2(_objectSpread2({}, getStateCSS(themeHoverProperties)), tp.timeIn && {
      transition: getTransition(tp.timeIn, tp.easing)
    }),
    ':active': getStateCSS(themeActiveProperties),
    ':disabled': _objectSpread2(_objectSpread2({}, getStateCSS(themeDisabledProperties)), {}, {
      cursor: 'not-allowed'
    }),
    ':focus': getStateCSS(themeFocusProperties)
  };

  /* Build CSS object combined with rules from props. */
  var blockStyles = {
    display: 'flex',
    width: '100%'
  };
  var hiddenRoundStyles = {
    borderRadius: '50%',
    padding: "".concat(pv, "px ").concat((sp.height - sp.iconSize - tp.borderWidth) / 2, "px")
  };
  // @ts-ignore
  var styles = [defaultCSS, statesCSS, block && blockStyles, hidden && tp.round && hiddenRoundStyles, css];

  /* Define CSS rules for icon. */
  var marginRule = "margin".concat(!iconAfter ? 'Right' : 'Left');
  var iconCSS = (_iconCSS = {}, _defineProperty(_iconCSS, marginRule, !hidden ? sp.iconOffset : undefined), _defineProperty(_iconCSS, "width", sp.iconSize), _defineProperty(_iconCSS, "height", sp.iconSize), _iconCSS);
  return jsx(as || 'button', _objectSpread2({
    ref: ref,
    type: !as || as === 'button' ? type : null,
    target: external ? '_blank' : null,
    rel: external ? 'nofollow noopener' : null,
    css: styles
  }, props), jsx(React__default.Fragment, null, Icon && !iconAfter && jsx(Icon, {
    css: iconCSS
  }), hidden ? jsx(VisuallyHidden, null, children) : children, Icon && iconAfter && jsx(Icon, {
    css: iconCSS
  })));
};
var getStateCSS = function getStateCSS(_ref2) {
  var color = _ref2.color,
    bg = _ref2.bg,
    border = _ref2.border,
    shadow = _ref2.shadow,
    css = _ref2.css;
  return _objectSpread2({
    color: color,
    fill: color,
    background: bg,
    borderColor: border,
    boxShadow: shadow
  }, css);
};
var getThemeProperties = function getThemeProperties(buttonTheme, theme, state) {
  var _buttonTheme$base;
  var themeProperties = buttonTheme.themes[theme][state];
  var baseProperties = (_buttonTheme$base = buttonTheme.base) === null || _buttonTheme$base === void 0 ? void 0 : _buttonTheme$base[state];
  return _objectSpread2(_objectSpread2({}, baseProperties), themeProperties);
};
var getVerticalPaddings = function getVerticalPaddings(typographyProperties, sizeProperties, themeProperties, isIcon) {
  var cssRule = sizeProperties.css;
  var fontSize = 16;
  var lineHeight = 1.4;
  if (typographyProperties) {
    fontSize = parseFloat(typographyProperties.fontSize) * 16;
    lineHeight = typographyProperties.lineHeight;
  } else if (cssRule) {
    if (cssRule.fontSize) {
      if (typeof cssRule.fontSize === 'number') {
        fontSize = cssRule.fontSize;
      } else if (typeof cssRule.fontSize === 'string') {
        if (cssRule.fontSize.endsWith('rem')) {
          fontSize = parseFloat(cssRule.fontSize) * 16;
        } else {
          fontSize = parseFloat(cssRule.fontSize);
        }
      }
    }
    if (cssRule.lineHeight && typeof cssRule.lineHeight === 'number') lineHeight = cssRule.lineHeight;
  }
  var textHeight = Math.floor(fontSize * lineHeight);
  var iconSize = sizeProperties.iconSize;
  var height = sizeProperties.height;
  var borderWidth = themeProperties.borderWidth;
  var maxHeight = Math.max(textHeight, isIcon ? iconSize : 0);
  var verticalPaddings = (height - maxHeight - borderWidth * 2) / 2;
  return verticalPaddings;
};
var getTransition = function getTransition(time, easing) {
  return ['color', 'fill', 'background-color', 'border-color', 'box-shadow'].map(function (name) {
    return "".concat(name, " ").concat(easing, " ").concat(time, "ms");
  }).join(', ');
};
var Button = /*#__PURE__*/React__default.forwardRef(Btn);

export { Button as B, Btn as a, useComponentTheme as u };
